<template>
  <div class="flex min-h-dvh w-full bg-slate-50">
    <!-- 桌面版側邊欄 (LG 以上) -->
    <aside class="hidden lg:flex w-64 flex-col bg-white border-r border-slate-100 shrink-0">
      <div class="flex-1 overflow-y-auto">
        <HamburgerMenu />
      </div>
    </aside>

    <!-- 右側主內容區 -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- 頂部導覽列 -->
      <header class="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-40 w-full">
        <!-- 兼顧環境變數與常規 Padding Fallback -->
        <div class="h-16 px-4 flex items-center justify-between mx-auto w-full max-w-(--content-max-width,1200px)"
          style="padding-top: max(0px, var(--safe-area-top, 0px));">
          <!-- 手機版漢堡按鈕：優化觸控點擊範圍與 active 狀態 -->
          <button @click="isSidebarOpen = true"
            class="lg:hidden p-2 -ml-2 text-slate-600 rounded-lg hover:bg-slate-100 active:bg-slate-200/80 transition-colors touch-manipulation"
            aria-label="Open Menu">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <span class="font-bold lg:hidden text-slate-800">{{ t('admin.dashboard') }}</span>

          <div class="flex-1"></div>

          <div class="flex items-center gap-3">
            <LanguageSelect />

            <button @click="handleLogout"
              class="text-xs font-bold text-red-600 px-4 py-2 rounded-xl bg-red-50 hover:bg-red-100 active:bg-red-200 transition-colors touch-manipulation">
              {{ t('admin.logout') }}
            </button>

            <NotificationManager v-if="isValidMerchant" :notificationApi="notificationApiImpl" :merchantId="merchantId"
              vapidKey="BE-2SnpV2ExMnuu5M8JR1QNz50ORIShqfhtCvi-2lTBw4dFrNza2Kbowq7W5mHULwN-3bWMiQwjWvnTBpSNrRIg" />
          </div>
        </div>
      </header>

      <!-- 主要內容呈現區：流式佈局 -->
      <main class="flex-1 w-full mx-auto max-w-(--content-max-width,1200px)">
        <div class="p-4 sm:p-6 lg:p-8" style="padding-bottom: calc(1.5rem + var(--safe-area-bottom, 0px));">
          <router-view />
        </div>
      </main>
    </div>

    <!-- 手機版側邊欄抽屜 (LG 以下) -->
    <Transition name="slide">
      <aside v-if="isSidebarOpen" class="fixed inset-0 z-100 lg:hidden flex justify-start">
        <!-- 遮罩背景 -->
        <div @click="isSidebarOpen = false" class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"></div>

        <!-- 抽屜主體：採用 max-w 防止極端螢幕撐滿，並允許獨立動態滾動 -->
        <div
          class="relative w-full max-w-[288px] min-h-dvh bg-white flex flex-col shadow-2xl z-10 overflow-y-auto overscroll-contain">
          <div class="flex-1">
            <HamburgerMenu @link-click="isSidebarOpen = false" />
          </div>
        </div>
      </aside>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/authStore'
import { useAuthFacade } from '@/auth/facade/authFacade'
import { logger } from '@myorg/type-lib'
import { STORAGE_KEYS } from '@/config/storageKeys'
import HamburgerMenu from './HamburgerMenu.vue'
import LanguageSelect from '@/components/LanguageSelect.vue'
import { NotificationManager } from '@myorg/notification-lib'
import { notificationApiImpl } from '@/services/notificationImpl'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const authFacade = useAuthFacade()

const isSidebarOpen = ref(false)
watch(() => route.path, () => isSidebarOpen.value = false)

const handleLogout = async () => {
  logger.log('[SidebarLayout.handleLogout] Logout button clicked')
  await authFacade.logout()
  logger.log('[SidebarLayout.handleLogout] authFacade.logout() completed')
}

// 響應式儲存空間存取與同步
const getMerchantId = () => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(STORAGE_KEYS.MERCHANT_ID)
}

const merchantId = ref(getMerchantId())

onMounted(() => {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEYS.MERCHANT_ID || !e.key) {
      merchantId.value = getMerchantId()
    }
  })
})

const isValidMerchant = computed(() => {
  return merchantId.value !== null &&
    merchantId.value !== undefined &&
    merchantId.value !== 'undefined' &&
    merchantId.value !== ''
})
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
